import {
  Component,
  inject,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
  Input,
} from '@angular/core';
import { InsightsService } from '../insights.service';
import { debounceTime, finalize, Subject, takeUntil } from 'rxjs';
import {
  ManageOrganizationDrawerParams,
  searchObj,
} from '../../manage-organization.interface';
import { OrgManagementMode } from '../../manage-organization.enums';
import {
  StudentListModel,
  UserApiResponsePaginated,
} from '../insights.interface';
import { CommonService } from '../../../../shared/services/common.service';
import { DRAWER_IDS } from '../../../../core/constants/drawer-ids.constant';
import { DrawerService } from '../../../../core/services/drawer.service';
import { ManageOrganizationService } from '../../manage-organization.service';
import { SvgIconComponent } from 'angular-svg-icon';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzDropDownModule } from 'ng-zorro-antd/dropdown';
import { NzTableModule, NzTableQueryParams } from 'ng-zorro-antd/table';
import { CommonModule } from '@angular/common';
import { ConfirmDialogComponent } from '../../../../components/confirm-dialog/confirm-dialog.component';
import { ErrorResponse } from '../../../../core/models/error.models';
@Component({
  selector: 'app-students',
  standalone: true,
  imports: [
    SvgIconComponent,
    NzButtonModule,
    NzDropDownModule,
    NzTableModule,
    CommonModule,
    ConfirmDialogComponent,
  ],
  templateUrl: './students.component.html',
})
export class StudentsComponent implements OnInit, OnDestroy {
  private readonly insightService = inject(InsightsService);
  @Input() tabIndex = 0;
  loading = false;
  pageSize = 10;
  pageIndex = 1;
  total = 0;
  sort = '';
  sortOrder: number | undefined = undefined;
  searchTerm = '';
  errorMessage = '';
  private readonly destroy$ = new Subject<void>();
  orgParams: ManageOrganizationDrawerParams = {
    organizationId: 0,
    mode: OrgManagementMode.VIEW,
  };
  organizationUsers$ = this.insightService.organizationUsers$;
  selectedRow: StudentListModel | null = null;

  listOfSelection = [
    {
      text: 'Select All Row',
      onSelect: () => {
        this.onAllChecked(true);
      },
    },
    {
      text: 'Select Odd Row',
      onSelect: () => {
        this.listOfCurrentPageData.forEach((data, index) =>
          this.updateCheckedSet(data.id, index % 2 !== 0)
        );
        this.refreshCheckedStatus();
      },
    },
    {
      text: 'Select Even Row',
      onSelect: () => {
        this.listOfCurrentPageData.forEach((data, index) =>
          this.updateCheckedSet(data.id, index % 2 === 0)
        );
        this.refreshCheckedStatus();
      },
    },
  ];
  checked = false;
  indeterminate = false;
  listOfCurrentPageData: readonly StudentListModel[] = [];
  listOfData: readonly StudentListModel[] = [];
  setOfCheckedId = new Set<number>();
  isVisible = false;
  isDeleting = false;
  deleteId = 0;

  constructor(
    private readonly commonService: CommonService,
    private readonly drawerService: DrawerService,
    private readonly manageOrdService: ManageOrganizationService,
    private readonly cdr: ChangeDetectorRef
  ) {
    this.setupDrawerParams();
    this.insightService.organizationUsers$.subscribe(
      (res: UserApiResponsePaginated | null) => {
        if (res) {
          this.total = res?.result.totalRows;
        }
      }
    );
  }

  onQueryParamsChange(params: NzTableQueryParams) {
    this.pageSize = params.pageSize;
    this.pageIndex = params.pageIndex;
    const currentSort = params.sort.find(item => item.value !== null);
    this.sort = currentSort?.key || '';
    const sortOrderValue = currentSort?.value === 'ascend' ? 0 : 1;
    this.sortOrder =
      !!currentSort && currentSort.value ? sortOrderValue : undefined;
    this.loadData();
  }

  updateCheckedSet(id: number, checked: boolean): void {
    if (checked) {
      this.setOfCheckedId.add(id);
    } else {
      this.setOfCheckedId.delete(id);
    }
  }

  onItemChecked(id: number, checked: boolean): void {
    this.updateCheckedSet(id, checked);
    this.refreshCheckedStatus();
  }

  onAllChecked(value: boolean): void {
    this.listOfCurrentPageData.forEach(item =>
      this.updateCheckedSet(item.id, value)
    );
    this.refreshCheckedStatus();
  }

  onCurrentPageDataChange($event: readonly StudentListModel[]): void {
    this.listOfCurrentPageData = $event;
    this.refreshCheckedStatus();
  }

  refreshCheckedStatus(): void {
    this.checked = this.listOfCurrentPageData.every(item =>
      this.setOfCheckedId.has(item.id)
    );
    this.indeterminate =
      this.listOfCurrentPageData.some(item =>
        this.setOfCheckedId.has(item.id)
      ) && !this.checked;
  }

  loadData(): void {
    this.loading = true;
    this.insightService
      .getOrgStudents({
        pageIndex: this.pageIndex,
        pageSize: this.pageSize,
        sortOrder: this.sortOrder,
        sort: this.sort,
        searchTerm: this.searchTerm,
        organizationId: Number(this.orgParams.organizationId),
      })
      .pipe(
        finalize(() => (this.loading = false)),
        takeUntil(this.destroy$)
      )
      .subscribe({
        error: ({ errors, message }: ErrorResponse) => {
          const errorMessages = errors.map(
            err => err.description || err.detailedMessage
          );
          this.commonService.notify('error', errorMessages, message);
        },
      });
  }

  ngOnInit() {
    this.manageOrdService.search$
      .pipe(debounceTime(1000))
      .subscribe((searchObj: searchObj) => {
        if (
          this.searchTerm !== searchObj.searchTerm &&
          searchObj.tabIndex === 2 &&
          this.tabIndex === 1
        ) {
          this.searchTerm = searchObj.searchTerm;
          if (this.pageIndex === 1) {
            this.loadData();
          } else {
            this.pageIndex = 1;
          }
        }
      });
  }

  private setupDrawerParams(): void {
    this.drawerService
      .getParams<ManageOrganizationDrawerParams>(DRAWER_IDS.MANAGE_ORG)
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        if (params) {
          this.orgParams = params;
        }
      });
  }

  showDeleteModal(data: StudentListModel) {
    this.isVisible = true;
    this.deleteId = data.id;
    this.selectedRow = data;
  }

  handleOkMiddle(): void {
    this.deleteStudent();
  }
  handleCancelMiddle(): void {
    this.isVisible = false;
    this.deleteId = 0;
    this.selectedRow = null;
  }

  deleteStudent() {
    this.isDeleting = true;
    this.insightService
      .deleteStudent(this.deleteId)
      .pipe(
        finalize(() => (this.isDeleting = false)),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: () => {
          this.loadData();
          this.cdr.detectChanges();
          this.commonService.notify('success', 'Student deleted successfully');
          this.isVisible = false;
          this.isDeleting = false;
          this.orgParams.mode = OrgManagementMode.VIEW;
          this.selectedRow = null;
        },
        error: error => {
          this.isVisible = false;
          this.isDeleting = false;
          this.commonService.notify('error', error.message);
          this.cdr.detectChanges();
        },
      });
    this.deleteId = 0;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
